<template>
  <div class="container-fluid shift-v3-page">
    <!-- Contract Header -->
    <HeaderContract
      :contract="model_contract"
      :filter-date.sync="filter_date"
      :employees="modelContractEmployees"
      :employee-total="modelContractEmployee_total"
    />

    <!-- Content -->
    <div class="row shift-v3-workspace">
      <!-- Calendar -->
      <div class="col-lg-9 shift-calendar-column">
        <div class="card shift-calendar-card">
          <div class="card-header shift-calendar-header">
            <div class="shift-calendar-title">
              <span class="shift-calendar-title__icon"><i class="ti ti-calendar"></i></span>
              <div>
                <strong>Shift Calendar</strong>
                <small v-if="selectedEmployee">
                  {{ selectedEmployee.firstname }} {{ selectedEmployee.lastname }} ·
                  {{ selectedEmployee.nik }}
                </small>
                <small v-else>Pilih teknisi untuk menampilkan jadwal.</small>
              </div>
            </div>
            <span class="shift-calendar-period">{{ filter_date }}</span>
          </div>

          <div class="card-body shift-calendar-body">
            <div
              v-if="shiftwork_employee_loading && selectedEmployee"
              class="calendar-skeleton"
              aria-busy="true"
              aria-label="Memuat kalender teknisi"
            >
              <div class="calendar-skeleton__toolbar">
                <div>
                  <span class="skeleton-line skeleton-line--label"></span>
                  <span class="skeleton-block skeleton-block--select"></span>
                </div>
                <span class="skeleton-block skeleton-block--button"></span>
              </div>
              <div class="calendar-skeleton__notice skeleton-block"></div>
              <div class="calendar-skeleton__header">
                <span
                  v-for="headerIndex in 8"
                  :key="'skeleton-header-' + headerIndex"
                  class="skeleton-block"
                ></span>
              </div>
              <div
                class="calendar-skeleton__week"
                v-for="weekIndex in 6"
                :key="'skeleton-week-' + weekIndex"
              >
                <span class="skeleton-block skeleton-block--week"></span>
                <span
                  v-for="dayIndex in 7"
                  :key="'skeleton-day-' + weekIndex + '-' + dayIndex"
                  class="skeleton-block skeleton-block--day"
                ></span>
              </div>
            </div>

            <div v-else-if="selectedEmployee">
              <div class="calendar-toolbar">
                  <div class="calendar-toolbar__field">
                    <label>Shift Pattern</label>

                    <select class="form-control" v-model="selectedPattern">
                      <option value="">Select Pattern</option>

                      <option
                        v-for="(shift, index) in model_shift_hour"
                        :key="shift.name + '-' + shift.hour + '-' + index"
                        :value="shift.name + ' - ' + shift.hour"
                      >
                        {{ shift.name }} - {{ shift.hour }}
                      </option>

                      <option value="OFF">OFF</option>

                      <option value="O">O</option>
                    </select>
                  </div>
                  <div class="calendar-toolbar__action">
                    <button
                      class="btn apply-pattern-btn"
                      @click="applyPattern"
                    >
                      <i class="ti ti-wand"></i> Apply Pattern
                    </button>
                  </div>
                  <div class="calendar-toolbar__hint">
                    <i class="ti ti-info-alt"></i>
                    <div>
                      <strong>Informasi</strong>
                      <span>Shift Pattern hanya diterapkan pada minggu yang dipilih.</span>
                    </div>
                  </div>
              </div>

              <div v-if="selectedDate" class="selected-date-toolbar mb-3">
                <div class="selected-date-info">
                  <i class="ti-pencil-alt"></i>

                  <div>
                    <small>Editing Schedule</small>

                    <strong>{{ selectedDate.date }}</strong>
                  </div>
                </div>

                <div class="selected-date-action">
                  <select
                    class="form-control"
                    :value="selectedDate.shift"
                    @change="changeSelectedDateShift($event)"
                  >
                    <option
                      v-for="(shift, index) in model_shift_hour"
                      :key="shift.name + '-' + index"
                      :value="shift.name + ' - ' + shift.hour"
                    >
                      {{ shift.name }} - {{ shift.hour }}
                    </option>

                    <option value="OFF">OFF</option>

                    <option value="O">O</option>
                  </select>
                </div>
              </div>

              <div class="pending-change-bar" v-if="hasPendingChanges">
                  <div class="pending-change-bar__info">
                    <i class="ti ti-alert"></i>
                    <div>
                      <strong>Perubahan belum disimpan</strong>
                      <small>Simpan jadwal sebelum berpindah teknisi atau periode.</small>
                    </div>
                  </div>
                  <div class="pending-change-bar__actions">
                    <button
                      class="btn pending-save-btn"
                      @click="savePendingChanges"
                    >
                      <i class="ti ti-save"></i> Save
                    </button>

                    <button
                      class="btn pending-cancel-btn"
                      @click="cancelPendingChanges"
                    >
                      Cancel
                    </button>
                  </div>
              </div>
              <div class="calendar-header">
                <div class="week-header">Week</div>
                <div
                  v-for="day in weekDays"
                  :key="day"
                  class="calendar-weekday"
                >
                  {{ day }}
                </div>
              </div>

              <div
                class="calendar-week"
                v-for="(week, indexWeek) in calendarWeeks"
                :key="indexWeek"
              >
                <!-- Week Action -->

                <div
                  class="week-action"
                  :class="{
                    active: selectedWeekIndex === indexWeek,
                  }"
                  @click="selectWeek(indexWeek)"
                >
                  <button
                    class="week-menu-button"
                    @click.stop="toggleWeekMenu(indexWeek)"
                  >
                    <i class="ti-more-alt"></i>
                  </button>

                  <small> W{{ indexWeek + 1 }} </small>

                  <div
                    v-if="activeWeekMenu === indexWeek"
                    class="week-dropdown"
                    @click.stop
                  >
                    <a @click="copyWeek(indexWeek)">
                      <i class="ti-files"></i>
                      Copy Week
                    </a>

                    <a v-if="clipboardWeek" @click="pasteWeek(indexWeek)">
                      <i class="ti-clipboard"></i>
                      Paste Here
                    </a>

                    <a class="text-danger" @click="clearWeek(indexWeek)">
                      <i class="ti-trash"></i>
                      Clear Week
                    </a>
                  </div>
                </div>

                <!-- Calendar -->

                <div
                  class="calendar-cell"
                  v-for="(item, indexDay) in week"
                  :key="indexDay"
                  :class="{
                    empty: !item,
                    selected:
                      selectedDate && item && selectedDate.date == item.date,
                  }"
                  @click="item && selectDate(item)"
                >
                  <span
                    v-if="
                      selectedDate && item && selectedDate.date === item.date
                    "
                    class="editing-badge"
                  >
                    <i class="ti-pencil"></i> Editing
                  </span>
                  <template v-if="item">
                    <div class="calendar-date">
                      {{ item.day }}
                    </div>

                    <div
                      class="calendar-shift"
                      :class="GetShiftClass(item.shift)"
                    >
                      {{ item.shift }}
                    </div>
                  </template>
                </div>
              </div>
            </div>
            <div v-else class="calendar-empty-state">
              <span><i class="ti ti-calendar"></i></span>
              <strong>Pilih teknisi terlebih dahulu</strong>
              <small>Kalender jadwal akan ditampilkan setelah teknisi dipilih.</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Employee -->
      <div class="col-lg-3 shift-sidebar">
        <PeriodPanel
          v-model="filter_date"
          :disabled-date="disabledBeforeAfterFilterDate"
          @change="FilterModelDate"
        />
        <TechnicianPanel
          :employees="modelContractEmployees"
          :total="modelContractEmployee_total"
          :selected="selectedEmployee"
          :loading="isLoadingMoreEmployees"
          :has-more="page < modelContractEmployee_lastpage"
          @select="selectEmployee"
          @load-more="loadMoreEmployees"
        />
      </div>
    </div>

    <!-- Modal -->
    <!-- Pending Dialog -->
    <div v-if="showPendingDialog" class="pending-backdrop">
      <div class="pending-dialog">
        <h5 class="mb-3">Perubahan Belum Disimpan</h5>

        <div class="alert alert-light mt-3">
          <template v-if="pendingNavigation.type === 'employee'">
            <strong>Tujuan :</strong>

            Teknisi

            <strong>{{ pendingNavigation.employee.firstname }}</strong>
          </template>

          <template v-else-if="pendingNavigation.type === 'period'">
            <strong>Tujuan :</strong>

            Periode

            <strong>{{ pendingNavigation.period }}</strong>
          </template>
        </div>

        <div class="text-right">
          <button class="btn btn-light mr-2" @click="stayHere">
            Tetap Disini
          </button>

          <button class="btn btn-warning mr-2" @click="cancelAndSwitch">
            Batalkan Perubahan
          </button>

          <button class="btn btn-success" @click="confirmSaveAndSwitch">
            Simpan & Pindah
          </button>
        </div>
      </div>
    </div>
    <!-- End Modal -->
  </div>
</template>
<script>
import axios from "axios";

import { ContentLoader, ListLoader } from "vue-content-loader";
import HeaderContract from "./components/HeaderContract.vue";
import PeriodPanel from "./components/PeriodPanel.vue";
import TechnicianPanel from "./components/TechnicianPanel.vue";

export default {
  components: {
    ContentLoader,
    ListLoader,
    HeaderContract,
    PeriodPanel,
    TechnicianPanel,
  },
  data() {
    return {
      isLoading: true,
      calendarWeeks: [],
      calendarOriginal: [],
      meta_url: "shiftwork",
      page: 1,
      collection_month: [],
      dateNowProject: null,
      modelContractEmployee_lastpage: 0,
      modelContractEmployee_frompage: 0,
      modelContractEmployee_total: 0,
      isLoadingMoreEmployees: false,
      isUpdateTools: false,
      model_contract: {},
      modelContractEmployees: [],
      selectedEmployee: null,
      shiftwork_employee: [],
      shiftwork_employee_loading: false,
      shiftworkRequestId: 0,
      model_shift_hour: [],
      shift_last_updated: "",
      filter_date: "",
      filter_month: "",
      filter_year: "",
      selectedPattern: "",
      applyType: "week",
      selectedDate: null,
      selectedWeekIndex: null,
      clipboardWeek: null,
      selectedWeek: null,
      hasPendingChanges: false,
      pendingChanges: {},
      activeWeekMenu: null,
      showPendingDialog: false,
      pendingNavigation: {
        type: null, // employee | period
        employee: null,
        period: null,
      },
      nextEmployee: null,
    };
  },
  computed: {
    weekDays() {
      return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    },
  },
  mounted() {
    this.QueryStartFromPeriod();

    document.addEventListener("click", () => {
      this.activeWeekMenu = null;
    });
  },
  watch: {
    shiftwork_employee() {
      this.generateCalendar();
    },
    selectedEmployee(newVal) {
      if (!newVal) return;

      this.getShiftWorkPerMonthByNik(newVal.nik);
    },
  },

  methods: {
    disabledBeforeAfterFilterDate(date) {
      const start_date = new Date(this.model_contract.start_date);
      start_date.setHours(0, 0, 0, 0);

      const end_date = new Date(this.model_contract.end_date);
      end_date.setHours(0, 0, 0, 0);

      let dateNow = new Date(date.getFullYear(), date.getMonth());
      let dateStart = new Date(start_date.getFullYear(), start_date.getMonth());
      let dateEnd = new Date(end_date.getFullYear(), end_date.getMonth());

      return dateNow < dateStart || dateNow > dateEnd;
    },
    changePeriod() {
      let fdate = this.filter_date.split("/");

      this.filter_month = fdate[0];
      this.filter_year = fdate[1];

      this.DataQuery();
    },
    FilterModelDate() {
      if (this.hasPendingChanges) {
        this.pendingNavigation = {
          type: "period",
          employee: null,
          period: this.filter_date,
        };

        this.showPendingDialog = true;

        return;
      }

      this.changePeriod();
    },
    async QueryStartFromPeriod() {
      await axios
        .get("contract/period/" + localStorage.getItem("QUERY_ID"))
        .then((response) => {
          const d = new Date();
          let query = response.data;
          let default_date = query.default;

          var dt = default_date.split("/");

          // this.collection_month = query.month_period;
          this.filter_month = ("0" + (d.getMonth() + 1)).slice(-2);
          this.filter_year = d.getFullYear().toString();

          this.filter_date = this.filter_month + "/" + this.filter_year;

          this.DataQuery();
        })
        .catch((error) => {
          console.log(error.statusText);
        })
        .finally(() => {
          // this.$store.state.loading.status = false;
        });
    },
    async DataQuery() {
      this.isLoading = true;
      this.opened = [];
      await axios
        .post("shiftwork/contract-v2", {
          id_contract: localStorage.getItem("QUERY_ID"),
          month: this.filter_month,
          year: this.filter_year,
        })
        .then((response) => {
          let query = response.data;

          // console.log(query);

          // this.dateNowProject = query.date;
          this.model_contract = query.contract;
          this.shift_last_updated = query.shift_last_updated.updated_at;
          this.model_shift_hour = query.shift_hour;
          //   console.log(JSON.stringify(this.model_shift_hour));

          // enable tools
          let block_status = ["terminate", "out_of_date"];
          if (!block_status.includes(this.model_contract.status)) {
            this.isUpdateTools = true;
          }

          // Add Default Shift
          if (!this.model_shift_hour) {
            this.model_shift_hour = new Array();
            this.model_shift_hour.push({ hour: "--:--", name: "Absent" });
          }

          // console.log(this.model_shift_hour);

          this.getEmployeeContractShiftWork();
          // this.isLoading = false;
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          // this.$store.state.loading.status = false;
        });
    },
    // async getEmployeeContractShiftWork(pageNum = 1) {
    //   this.isLoading = true;
    //   this.page = pageNum;
    //   await axios
    //     .post("shiftwork/employee-v2?page=" + this.page, {
    //       id_contract: localStorage.getItem("QUERY_ID"),
    //       month: this.filter_month,
    //       year: this.filter_year,
    //     })
    //     .then((response) => {
    //       let query = response.data.result;

    //       this.modelContractEmployees = query.data;
    //       this.modelContractEmployee_lastpage = query.last_page;
    //       this.modelContractEmployee_frompage = query.from;

    //       this.restoreSelectedEmployee();

    //       this.isLoading = false;
    //     })
    //     .catch((error) => {
    //       console.log(error.statusText);
    //     });
    // },
    async getEmployeeContractShiftWork(pageNum = 1, append = false) {
      if (append && this.isLoadingMoreEmployees) return;

      if (append) {
        this.isLoadingMoreEmployees = true;
      } else {
        this.isLoading = true;
      }

      this.page = pageNum;

      try {
        const response = await axios.post(
          "shiftwork/employee-v2?page=" + this.page,
          {
            id_contract: localStorage.getItem("QUERY_ID"),
            month: this.filter_month,
            year: this.filter_year,
          }
        );

        let query = response.data.result;

        const employeePage = query.data || [];

        if (append) {
          const loadedNik = new Set(
            this.modelContractEmployees.map((employee) => employee.nik)
          );
          this.modelContractEmployees = this.modelContractEmployees.concat(
            employeePage.filter((employee) => !loadedNik.has(employee.nik))
          );
        } else {
          this.modelContractEmployees = employeePage;
        }

        this.modelContractEmployee_lastpage = query.last_page;

        this.modelContractEmployee_frompage = query.from;

        this.modelContractEmployee_total = Number(query.total || 0);

        if (!append) {
          this.restoreSelectedEmployee();
        }
      } catch (error) {
        console.log(error);
      } finally {
        if (append) {
          this.isLoadingMoreEmployees = false;
        } else {
          this.isLoading = false;
        }
      }
    },
    loadMoreEmployees() {
      if (
        this.isLoadingMoreEmployees ||
        this.page >= this.modelContractEmployee_lastpage
      ) {
        return;
      }

      this.getEmployeeContractShiftWork(this.page + 1, true);
    },
    restoreSelectedEmployee() {
      if (!this.selectedEmployee) {
        if (this.modelContractEmployees.length > 0) {
          const employee = this.modelContractEmployees[0];

          this.selectedEmployee = employee;
        }

        return;
      }

      const employee = this.modelContractEmployees.find(
        (x) => x.nik === this.selectedEmployee.nik
      );

      if (!employee) {
        this.selectedEmployee = null;
        this.calendarWeeks = [];
        this.selectedDate = null;

        return;
      }

      this.selectedEmployee = employee;
    },
    selectEmployee(item) {
      // klik teknisi yang sama
      if (this.selectedEmployee && this.selectedEmployee.nik === item.nik) {
        return;
      }

      // ada perubahan
      // if (this.hasPendingChanges) {
      //   this.nextEmployee = item;

      //   this.showPendingDialog = true;

      //   return;
      // }
      if (this.hasPendingChanges) {
        this.pendingNavigation = {
          type: "employee",
          employee: item,
          period: null,
        };

        this.showPendingDialog = true;
        return;
      }

      this.selectedEmployee = item;
    },
    loadSelectedEmployeeCalendar() {
      if (!this.selectedEmployee) return;

      this.getShiftWorkPerMonthByNik(this.selectedEmployee.nik);
    },
    async getShiftWorkPerMonthByNik(nik) {
      this.shiftwork_employee_loading = true;
      this.shiftwork_employee = [];

      const requestId = ++this.shiftworkRequestId;

      try {
        const response = await axios.post("shiftwork/date-range-v2", {
          id_contract: localStorage.getItem("QUERY_ID"),
          month: this.filter_month,
          year: this.filter_year,
          nik: nik,
        });

        if (requestId !== this.shiftworkRequestId) return;

        const result = response.data.result;
        this.shiftwork_employee = result.date_range_shiftwork;
      } catch (error) {
        if (requestId !== this.shiftworkRequestId) return;

        const message =
          error.response && error.response.data
            ? error.response.data.msg
            : "Gagal memuat kalender teknisi.";
        alert(message);
      } finally {
        if (requestId === this.shiftworkRequestId) {
          this.shiftwork_employee_loading = false;
        }
      }
    },
    GetShiftClass(shift) {
      switch (shift) {
        case "OFF":
          return "shift-off";
        case "O":
          return "shift-o";
        case "Absent":
          return "shift-absent";
        default:
          return "shift-normal";
      }
    },
    generateCalendar() {
      if (!this.shiftwork_employee.length) {
        this.calendarWeeks = [];
        return;
      }

      const year = Number(this.filter_year);
      const month = Number(this.filter_month);

      const firstDate = new Date(year, month - 1, 1);

      let firstDay = firstDate.getDay();

      // Monday First
      firstDay = firstDay === 0 ? 6 : firstDay - 1;

      const calendar = [];

      let week = [];

      // padding minggu pertama

      for (let i = 0; i < firstDay; i++) {
        week.push(null);
      }

      // isi tanggal

      this.shiftwork_employee.forEach((day) => {
        week.push(day);

        if (week.length === 7) {
          calendar.push(week);

          week = [];
        }
      });

      // padding minggu terakhir

      while (week.length < 7 && week.length > 0) {
        week.push(null);
      }

      if (week.length) {
        calendar.push(week);
      }

      //   this.calendarWeeks = calendar;
      this.setCalendar(calendar);
    },
    selectDate(item) {
      this.selectedDate = item;

      this.selectedWeekIndex = this.getSelectedWeekIndex();
    },
    selectWeek(index) {
      this.selectedWeekIndex = index;
    },
    getWeek(index) {
      return this.calendarWeeks[index] || [];
    },
    getValidDaysInWeek(index) {
      return this.calendarWeeks[index].filter((day) => day);
    },
    getSelectedWeekIndex() {
      if (!this.selectedDate) return -1;

      return this.calendarWeeks.findIndex((week) =>
        week.some((day) => day && day.date === this.selectedDate.date)
      );
    },
    copyWeek(index) {
      this.clipboardWeek = JSON.parse(
        JSON.stringify(this.calendarWeeks[index])
      );

      this.activeWeekMenu = null;

      this.$store.state.alert.status = true;
      this.$store.state.alert.type = "success";
      this.$store.state.alert.msg = "Week copied.";
    },
    pasteWeek(index) {
      if (!this.clipboardWeek) return;

      const target = this.calendarWeeks[index];

      this.clipboardWeek.forEach((source, i) => {
        if (!source || !target[i]) return;

        this.updateCalendarShift(
          target[i],

          source.shift
        );
      });
    },
    clearWeek(index) {
      const week = this.getValidDaysInWeek(index);

      week.forEach((day) => {
        this.updateCalendarShift(day, "O");
      });

      this.activeWeekMenu = null;
    },
    toggleWeekMenu(index) {
      if (this.activeWeekMenu === index) {
        this.activeWeekMenu = null;

        return;
      }

      this.activeWeekMenu = index;
    },
    cancelPendingChanges() {
      this.pendingChanges = [];
      this.hasPendingChanges = false;
      this.getShiftWorkPerMonthByNik(this.selectedEmployee.nik);
    },
    splitShift(value) {
      if (value === "OFF" || value === "O") {
        return {
          shift: value,
          hour: null,
        };
      }

      const arr = value.split(" - ");

      return {
        shift: arr[0].trim(),
        hour: arr[1].trim(),
      };
    },
    async savePendingChanges() {
      const schedule = Object.values(this.pendingChanges).map((item) => {
        const shift = this.splitShift(item.newShift);

        return {
          id: item.id,
          nik: item.nik,
          date: item.date,
          shift: shift.shift,
          hour: shift.hour,
        };
      });

      const payload = {
        id_contract: localStorage.getItem("QUERY_ID"),
        schedule,
      };

      try {
        const response = await axios.post("shiftwork/assign-v2.2", payload);

        this.pendingChanges = {};
        this.hasPendingChanges = false;

        this.calendarOriginal = JSON.parse(JSON.stringify(this.calendarWeeks));

        this.$store.state.alert.status = true;
        this.$store.state.alert.type = "success";
        this.$store.state.alert.msg = response.data.msg;
      } catch (error) {
        console.error(error);

        this.$store.state.alert.status = true;
        this.$store.state.alert.type = "trash";
        if (error.response && error.response.data) {
          this.$store.state.alert.msg = error.response.data.msg;
        } else {
          this.$store.state.alert.msg = "Gagal menyimpan perubahan.";
        }
        return false;
      }

      return true;
    },
    async confirmSaveAndSwitch() {
      const success = await this.savePendingChanges();

      if (!success) return;

      // this.selectedEmployee = this.nextEmployee;

      if (this.pendingNavigation.type === "employee") {
        this.selectedEmployee = this.pendingNavigation.employee;
      }

      if (this.pendingNavigation.type === "period") {
        this.changePeriod();
      }

      this.pendingNavigation = {
        type: null,
        employee: null,
        period: null,
      };

      // this.nextEmployee = null;

      this.showPendingDialog = false;
    },
    cancelAndSwitch() {
      this.cancelPendingChanges();

      // this.selectedEmployee = this.nextEmployee;

      if (this.pendingNavigation.type === "employee") {
        this.selectedEmployee = this.pendingNavigation.employee;
      }

      if (this.pendingNavigation.type === "period") {
        this.changePeriod();
      }

      this.pendingNavigation = {
        type: null,
        employee: null,
        period: null,
      };

      this.showPendingDialog = false;

      // this.nextEmployee = null;

      // this.showPendingDialog = false;
    },
    stayHere() {
      // this.nextEmployee = null;

      this.pendingNavigation = {
        type: null,
        employee: null,
        period: null,
      };

      this.showPendingDialog = false;
    },
    setCalendar(calendar) {
      this.calendarOriginal = JSON.parse(JSON.stringify(calendar));
      this.calendarWeeks = calendar;
    },
    updatePendingChange(item, newShift) {
      if (!item) return;

      const key = item.date;

      const original = this.calendarOriginal
        .flat()
        .find((x) => x && x.date === key);

      if (!original) return;

      // Jika shift kembali seperti awal
      if (original.shift === newShift) {
        delete this.pendingChanges[key];
      } else {
        this.pendingChanges[key] = {
          id: item.id,
          date: item.date,
          nik: this.selectedEmployee.nik,
          oldShift: original.shift,
          newShift: newShift,
          status: item.status,
        };
      }

      this.hasPendingChanges = Object.keys(this.pendingChanges).length > 0;
    },
    updateCalendarShift(item, newShift) {
      if (!item) return;

      // Tidak ada perubahan
      if (item.shift === newShift) {
        return;
      }

      // Update UI
      item.shift = newShift;

      if (this.selectedDate && this.selectedDate.date === item.date) {
        this.selectedDate.shift = newShift;
      }

      // Update Pending
      this.updatePendingChange(item, newShift);
    },
    changeSelectedDateShift(event) {
      if (!this.selectedDate) return;

      this.updateCalendarShift(this.selectedDate, event.target.value);
    },
    applyPattern() {
      if (!this.selectedPattern) {
        this.$store.state.alert.status = true;
        this.$store.state.alert.type = "warning";
        this.$store.state.alert.msg = "Please select shift pattern.";

        return;
      }

      if (!this.selectedDate) {
        this.$store.state.alert.status = true;
        this.$store.state.alert.type = "warning";
        this.$store.state.alert.msg = "Please select date.";

        return;
      }

      // const shift = this.formatShift(this.selectedPattern);
      const shift = this.selectedPattern;

      if (this.applyType === "week") {
        const weekIndex = this.getSelectedWeekIndex();

        if (weekIndex === -1) return;

        this.getValidDaysInWeek(weekIndex).forEach((day) => {
          this.updateCalendarShift(day, shift);
        });
      }
    },
  },
};
</script>


<style scoped>
.card {
  border-radius: 12px;
}

.card-header {
  background: #fff;
  font-weight: 600;
}

.calendar-skeleton {
  min-height: 650px;
}

.calendar-skeleton__toolbar {
  align-items: flex-end;
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.calendar-skeleton__toolbar > div {
  display: flex;
  flex-direction: column;
  width: 31%;
}

.skeleton-line,
.skeleton-block {
  animation: calendar-skeleton-pulse 1.25s ease-in-out infinite;
  background: linear-gradient(90deg, #edf2f4 25%, #f7fafb 45%, #e8eef1 65%);
  background-size: 220% 100%;
  border-radius: 7px;
  display: block;
}

.skeleton-line--label {
  height: 10px;
  margin-bottom: 8px;
  width: 82px;
}

.skeleton-block--select {
  height: 38px;
  width: 100%;
}

.skeleton-block--button {
  height: 38px;
  width: 31%;
}

.calendar-skeleton__notice {
  height: 52px;
  margin-bottom: 24px;
  width: 100%;
}

.calendar-skeleton__header,
.calendar-skeleton__week {
  display: grid;
  gap: 10px;
  grid-template-columns: 80px repeat(7, 1fr);
  margin-bottom: 10px;
}

.calendar-skeleton__header .skeleton-block {
  height: 42px;
}

.skeleton-block--week,
.skeleton-block--day {
  min-height: 120px;
}

.skeleton-block--week {
  border-radius: 10px;
}

.skeleton-block--day {
  border-radius: 10px;
}

@keyframes calendar-skeleton-pulse {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

.calendar-placeholder {
  height: 650px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 2px dashed #d9d9d9;
  border-radius: 12px;
  color: #999;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  gap: 12px;
}

.calendar-header {
  display: grid;
  grid-template-columns: 80px repeat(7, 1fr);
  gap: 10px;
  margin-bottom: 10px;
}

.calendar-weekday {
  text-align: center;
  font-weight: 700;
  padding: 10px;
  background: #f8f9fa;
  border: 1px solid #ececec;
}

.calendar-week {
  display: grid;
  grid-template-columns: 80px repeat(7, 1fr);
  gap: 10px;
  margin-bottom: 10px;
}

.selected-date-toolbar {
  display: flex;

  align-items: center;

  justify-content: space-between;

  background: #f8fbff;

  border: 1px solid #dbeafe;

  border-radius: 10px;

  padding: 12px 16px;
}

.selected-date-info {
  display: flex;

  align-items: center;
}

.selected-date-info i {
  font-size: 18px;

  color: #0d6efd;

  margin-right: 12px;
}

.selected-date-info small {
  display: block;

  color: #888;
}

.selected-date-info strong {
  font-size: 15px;
}

.selected-date-action {
  width: 260px;
}

.week-menu {
  position: fixed;
  width: 180px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.18);
  z-index: 99999;
}

.week-menu-item {
  padding: 12px 15px;
  cursor: pointer;
}

.week-menu-item:hover {
  background: #f5f5f5;
}

.week-column {
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  background: #f8f9fa;
  border: 1px solid #ececec;
  border-radius: 8px;
}

.week-action {
  position: relative;
  border: 1px solid #ececec;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #fff;
  min-height: 120px;
}

.week-action:hover {
  border-color: #007bff;
  background: #eef6ff;
}

.week-action.active {
  border: 2px solid #28a745;
  background: #f1fbf4;
}

.week-action.active small {
  color: #28a745;
  font-weight: bold;
}

.week-menu-button {
  border: none;
  background: none;
  font-size: 18px;
  cursor: pointer;
  margin-bottom: 8px;
}

.week-dropdown {
  position: absolute;
  top: 35px;
  left: 70px;
  width: 180px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  overflow: hidden;
}

.week-dropdown a {
  display: block;
  padding: 12px;
  cursor: pointer;
  color: #333;
  text-decoration: none;
}

.week-dropdown a:hover {
  background: #f5f7fa;
}

.calendar-cell {
  min-height: 120px;
  border: 1px solid #ececec;
  border-radius: 10px;
  padding: 10px;
  background: #fff;
  transition: 0.2s;

  position: relative;
}

.editing-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: #777;
  color: white;
  font-size: 10px;
  padding: 3px 8px;
  border-radius: 30px;
  font-weight: 600;
}

.calendar-cell:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.calendar-cell.selected {
  border: 2px solid #0d6efd;
  background: #f8fbff;
  box-shadow: 0 0 0 4px rgba(13, 110, 253, 0.12);
}

.calendar-cell.selected .calendar-shift {
  transform: scale(1.05);
  font-weight: bold;
}

.calendar-cell.empty {
  border: none;
  background: transparent;
  box-shadow: none;
}

.calendar-date {
  font-size: 20px;
  font-weight: bold;
}

.calendar-shift {
  margin-top: 15px;
  text-align: center;
  border-radius: 20px;
  padding: 6px;
  font-weight: 600;
}

.calendar-item {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 10px;
  text-align: center;
  transition: 0.2s;
}

.calendar-item:hover {
  border-color: #0d6efd;
  transform: translateY(-2px);
}
.calendar-day {
  font-size: 22px;
  font-weight: 700;
}
.calendar-week {
  font-size: 12px;
  color: #777;
  margin-bottom: 10px;
}
.calendar-shift {
  border-radius: 20px;
  padding: 4px;
  font-size: 12px;
  font-weight: 600;
}
.shift-normal {
  background: #e8f5e9;
  color: #2e7d32;
}

.shift-off {
  background: #ffebee;
  color: #d32f2f;
}

.shift-o {
  background: #fff8e1;
  color: #f57f17;
}

.shift-absent {
  background: #eceff1;
  color: #455a64;
}

.pending-backdrop {
  position: fixed;

  inset: 0;

  background: rgba(0, 0, 0, 0.45);

  display: flex;

  justify-content: center;

  align-items: center;

  z-index: 99999;
}

.pending-dialog {
  width: 500px;
  background: #fff;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.25);
}

.shift-v3-page {
  color: #344a58;
  padding-bottom: 32px;
  padding-left: 0;
  padding-right: 0;
}

.shift-v3-workspace {
  align-items: stretch;
  margin-left: -7px;
  margin-right: -7px;
}

.shift-calendar-column,
.shift-sidebar {
  display: flex;
  flex-direction: column;
  padding-left: 7px;
  padding-right: 7px;
}

.shift-sidebar {
  align-self: stretch;
  min-height: 0;
}

.shift-calendar-card {
  border: 1px solid #e1e9ee;
  border-radius: 13px;
  box-shadow: 0 9px 25px rgba(47, 89, 111, 0.08);
  flex: 1 1 auto;
  margin-bottom: 0;
  overflow: visible;
}

.shift-sidebar > .technician-card {
  flex: 1 1 0;
  min-height: 0;
}

.shift-calendar-header {
  align-items: center;
  background: #fff;
  border-bottom: 1px solid #e8eef2;
  border-radius: 13px 13px 0 0;
  display: flex;
  justify-content: space-between;
  padding: 14px 16px;
}

.shift-calendar-title {
  align-items: center;
  display: flex;
  gap: 10px;
}

.shift-calendar-title__icon {
  align-items: center;
  background: #eaf6f7;
  border-radius: 9px;
  color: #318a99;
  display: flex;
  font-size: 17px;
  height: 36px;
  justify-content: center;
  width: 36px;
}

.shift-calendar-title strong,
.shift-calendar-title small {
  display: block;
}

.shift-calendar-title strong {
  color: #344f60;
  font-size: 13px;
}

.shift-calendar-title small {
  color: #8396a2;
  font-size: 9px;
  margin-top: 2px;
}

.shift-calendar-period {
  background: #f1f7f9;
  border: 1px solid #dce9ed;
  border-radius: 14px;
  color: #397788;
  font-size: 10px;
  font-weight: 700;
  padding: 5px 10px;
}

.shift-calendar-body {
  background: #fbfcfd;
  border-radius: 0 0 13px 13px;
  padding: 16px;
}

.calendar-toolbar {
  align-items: end;
  background: #f5f9fb;
  border: 1px solid #e0e9ee;
  border-radius: 10px;
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(220px, 1fr) auto;
  margin-bottom: 13px;
  padding: 13px;
}

.calendar-toolbar__field label {
  color: #607683;
  display: block;
  font-size: 9px;
  font-weight: 700;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.calendar-toolbar__field .form-control,
.selected-date-action .form-control {
  border: 1px solid #d6e1e7;
  border-radius: 7px;
  box-shadow: none;
  min-height: 38px;
}

.apply-pattern-btn {
  align-items: center;
  background: #3193a7;
  border: 1px solid #3193a7;
  border-radius: 7px;
  color: #fff;
  display: flex;
  font-size: 11px;
  font-weight: 600;
  gap: 7px;
  justify-content: center;
  min-height: 38px;
  min-width: 170px;
}

.apply-pattern-btn:hover {
  background: #287f91;
  color: #fff;
}

.calendar-toolbar__hint {
  align-items: center;
  background: #eaf7f7;
  border: 1px solid #cbe7e7;
  border-radius: 8px;
  color: #3b727a;
  display: flex;
  gap: 10px;
  grid-column: 1 / -1;
  padding: 9px 11px;
}

.calendar-toolbar__hint > i {
  color: #3193a7;
}

.calendar-toolbar__hint strong,
.calendar-toolbar__hint span {
  display: block;
}

.calendar-toolbar__hint strong {
  font-size: 10px;
}

.calendar-toolbar__hint span {
  font-size: 9px;
  margin-top: 1px;
}

.selected-date-toolbar {
  background: #f0f7fb;
  border: 1px solid #d4e5ee;
  border-radius: 9px;
  padding: 10px 13px;
}

.selected-date-info i {
  align-items: center;
  background: #deedf5;
  border-radius: 7px;
  color: #397e9a;
  display: flex;
  height: 31px;
  justify-content: center;
  margin-right: 9px;
  width: 31px;
}

.selected-date-info small {
  color: #8195a1;
  font-size: 9px;
}

.selected-date-info strong {
  color: #36576a;
  font-size: 12px;
}

.pending-change-bar {
  align-items: center;
  background: #fff8e7;
  border: 1px solid #f1d891;
  border-radius: 9px;
  display: flex;
  justify-content: space-between;
  margin-bottom: 13px;
  padding: 10px 12px;
}

.pending-change-bar__info {
  align-items: center;
  color: #885f13;
  display: flex;
  gap: 9px;
}

.pending-change-bar__info > i {
  font-size: 17px;
}

.pending-change-bar__info strong,
.pending-change-bar__info small {
  display: block;
}

.pending-change-bar__info strong {
  font-size: 10px;
}

.pending-change-bar__info small {
  color: #a17d3a;
  font-size: 9px;
}

.pending-change-bar__actions {
  display: flex;
  gap: 6px;
}

.pending-save-btn,
.pending-cancel-btn {
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
  padding: 6px 10px;
}

.pending-save-btn {
  background: #319b75;
  color: #fff;
}

.pending-cancel-btn {
  background: #fff;
  border: 1px solid #e3cf9c;
  color: #8f6c24;
}

.calendar-header {
  gap: 7px;
  grid-template-columns: 64px repeat(7, minmax(74px, 1fr));
  margin-bottom: 7px;
}

.week-header,
.calendar-weekday {
  align-items: center;
  background: #edf6f8;
  border: 1px solid #dce9ed;
  border-radius: 7px;
  color: #526d7b;
  display: flex;
  font-size: 9px;
  font-weight: 700;
  justify-content: center;
  min-height: 36px;
  padding: 7px;
  text-transform: uppercase;
}

.calendar-week {
  color: #526674;
  gap: 7px;
  grid-template-columns: 64px repeat(7, minmax(74px, 1fr));
  margin-bottom: 7px;
}

.week-action {
  background: #f8fbfc;
  border: 1px solid #dfe9ee;
  border-radius: 8px;
  min-height: 98px;
}

.week-action:hover {
  background: #edf8f7;
  border-color: #a9d6d2;
}

.week-action.active {
  background: #eaf7f3;
  border: 1px solid #3aa486;
  box-shadow: 0 0 0 3px rgba(58, 164, 134, 0.1);
}

.week-action.active small {
  color: #268169;
}

.week-menu-button {
  align-items: center;
  background: #fff;
  border: 1px solid #dce6eb;
  border-radius: 50%;
  color: #607986;
  display: flex;
  font-size: 12px;
  height: 27px;
  justify-content: center;
  margin-bottom: 7px;
  width: 27px;
}

.week-dropdown {
  border: 1px solid #e0e7eb;
  box-shadow: 0 12px 28px rgba(36, 65, 82, 0.18);
  left: 48px;
  top: 30px;
  width: 155px;
}

.week-dropdown a {
  color: #445e6c;
  font-size: 10px;
  padding: 9px 11px;
}

.calendar-cell {
  background: #fff;
  border: 1px solid #e1e8ed;
  border-radius: 8px;
  min-height: 98px;
  padding: 9px;
}

.calendar-cell:hover {
  border-color: #9dcfd1;
  box-shadow: 0 5px 14px rgba(49, 109, 128, 0.1);
}

.calendar-cell.selected {
  background: #f1f8fb;
  border: 1px solid #3c91ad;
  box-shadow: 0 0 0 3px rgba(60, 145, 173, 0.11);
}

.calendar-date {
  color: #536b78;
  font-size: 17px;
}

.calendar-shift {
  font-size: 10px;
  line-height: 1.35;
  margin-top: 12px;
  padding: 5px 6px;
}

.shift-normal {
  background: #e7f6ed;
  color: #277649;
}

.shift-off {
  background: #fff0ef;
  color: #b8534d;
}

.shift-o {
  background: #fff7df;
  color: #a97312;
}

.shift-absent {
  background: #edf1f3;
  color: #60737e;
}

.editing-badge {
  background: #397f99;
  font-size: 8px;
  padding: 3px 6px;
  right: 6px;
  top: 6px;
}

.calendar-empty-state {
  align-items: center;
  color: #8496a1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 560px;
  text-align: center;
}

.calendar-empty-state > span {
  align-items: center;
  background: #eaf6f7;
  border-radius: 50%;
  color: #388d9c;
  display: flex;
  font-size: 27px;
  height: 66px;
  justify-content: center;
  margin-bottom: 13px;
  width: 66px;
}

.calendar-empty-state strong {
  color: #4c6472;
  font-size: 13px;
  margin-bottom: 4px;
}

.calendar-empty-state small {
  font-size: 10px;
}

.pending-backdrop {
  backdrop-filter: blur(3px);
  background: rgba(19, 40, 53, 0.55);
}

.pending-dialog {
  border: 1px solid #e3eaee;
  border-radius: 14px;
  box-shadow: 0 22px 60px rgba(19, 41, 55, 0.28);
  max-width: calc(100vw - 30px);
}

@media (max-width: 991px) {
  .shift-sidebar {
    display: block;
    margin-top: 14px;
  }

  .shift-sidebar > .technician-card {
    min-height: 360px;
  }
}

@media (max-width: 767px) {
  .shift-v3-page {
    padding-bottom: 22px;
  }

  .shift-calendar-header,
  .pending-change-bar,
  .selected-date-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .shift-calendar-period {
    margin-top: 10px;
    text-align: center;
  }

  .shift-calendar-body {
    overflow-x: auto;
    padding: 12px;
  }

  .calendar-toolbar {
    grid-template-columns: 1fr;
    min-width: 650px;
  }

  .calendar-toolbar__hint {
    grid-column: auto;
  }

  .apply-pattern-btn {
    width: 100%;
  }

  .selected-date-action {
    margin-top: 10px;
    width: 100%;
  }

  .pending-change-bar__actions {
    margin-top: 10px;
  }

  .calendar-header,
  .calendar-week,
  .calendar-skeleton__header,
  .calendar-skeleton__week {
    min-width: 650px;
  }
}
</style>
